import { PureComponent, ReactNode } from 'react';

import { ABHA_IDS, CONTACT, FOLLOW_UP, LAST_VISITED, PT_DETAILS, UHID } from '../constants';
import { Button, Popover, Radio, Space } from 'antd';
import { CustomDatePicker } from '../components/common/DatePicker';
import { Moment } from 'moment';

type ListMode = 'quick' | 'full' | 'y-followup' | 't-followup' | 'custom-followup';

type Props = {
    columns: { key: string; label: string }[];
    mode: ListMode;
    abhaFilterType?: 'present' | 'absent';
    referredFilterType?: 'referred';
    openTableConfig: () => void;
    onRadioOptionChange: (mode: ListMode) => void;
    onAbhaOptionChange: (type?: 'present' | 'absent') => void;
    onRefferedRadioOptionChange: (type?: 'referred') => void;
    dates?: [Moment, Moment];
    onDateChange: (startDate: string, endDate: string) => void;
    onModeChange: (mode: ListMode) => void;
};

class AllPtTableHeader extends PureComponent<Props> {
    followUpFilter = (): ReactNode => (
        <div>
            <Radio.Group
                onChange={(e) => this.props.onRadioOptionChange(e.target.value)}
                value={this.props.mode}
            >
                <Space direction="vertical">
                    <Radio value={'y-followup'}>Yesterday</Radio>
                    <Radio value={'t-followup'}>Today</Radio>
                    <Radio value={'custom-followup'}>
                        <CustomDatePicker
                            dates={this.props.dates}
                            onDatesSelected={this.props.onDateChange}
                            noOfDays={Number.MAX_SAFE_INTEGER}
                            enableFuture={true}
                        />
                    </Radio>
                </Space>
            </Radio.Group>
        </div>
    );

    abhaFilter = (): ReactNode => (
        <div>
            <Radio.Group
                onChange={(e) => this.props.onAbhaOptionChange(e.target.value)}
                value={this.props.abhaFilterType}
            >
                <Space direction="vertical">
                    <Radio value={'present'}>ABHA linked</Radio>
                    <Radio value={'absent'}>ABHA not linked</Radio>
                </Space>
            </Radio.Group>
        </div>
    );

    referralFilter = (): ReactNode => (
        <div>
            <Radio.Group
                onChange={(e) => this.props.onRefferedRadioOptionChange(e.target.value)}
                value={this.props.referredFilterType}
            >
                <Space direction="vertical">
                    <Radio value={'referred'}>Referred</Radio>
                </Space>
            </Radio.Group>
        </div>
    );

    followUpTitle = (): ReactNode => {
        return (
            <div className="flex justify-between w-full items-center">
                <div className="font-400 text-14 bold">Filter</div>
                <Button type="link" onClick={() => this.props.onModeChange('quick')}>
                    Clear
                </Button>
            </div>
        );
    };

    abhaTitle = (): ReactNode => {
        return (
            <div className="flex justify-between w-full items-center">
                <div className="font-400 text-14 bold">Filter</div>
                <Button type="link" onClick={() => this.props.onAbhaOptionChange(undefined)}>
                    Clear
                </Button>
            </div>
        );
    };

    referralTitle = (): ReactNode => {
        return (
            <div className="flex justify-between w-full items-center">
                <div className="font-400 text-14 bold">Filter</div>
                <Button
                    type="link"
                    onClick={() => this.props.onRefferedRadioOptionChange(undefined)}
                >
                    Clear
                </Button>
            </div>
        );
    };

    render(): ReactNode {
        const { columns, openTableConfig, mode, abhaFilterType } = this.props;
        return (
            <div
                className="w-full mb-8"
                style={{ backgroundColor: 'rgb(235,238,242)', color: 'rgba(44,58,75,1)' }}
            >
                <div className="flex flex-auto flex-1 w-full text-12 font-700 py-12 justify-between ">
                    <div className="text-center w-56 px-8">
                        <p>Sr. No.</p>
                    </div>

                    {columns.map((c) => {
                        switch (c.key) {
                            case PT_DETAILS:
                                return (
                                    <div
                                        style={{ width: 330 }}
                                        className="flex items-center space-x-4 text-left pr-16 px-8"
                                    >
                                        <p>{c.label}</p>
                                        <Popover
                                            content={this.referralFilter()}
                                            title={this.referralTitle()}
                                            placement="right"
                                        >
                                            {abhaFilterType ? (
                                                <svg width="14" height="14" viewBox="0 0 256 256">
                                                    <defs></defs>
                                                    <g transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
                                                        <path
                                                            d="M 52.537 80.466 V 45.192 L 84.53 2.999 C 85.464 1.768 84.586 0 83.041 0 H 6.959 C 5.414 0 4.536 1.768 5.47 2.999 l 31.994 42.192 v 43.441 c 0 1.064 1.163 1.719 2.073 1.167 l 11.758 -7.127 C 52.065 82.205 52.537 81.368 52.537 80.466 z"
                                                            transform=" matrix(1 0 0 1 0 0) "
                                                            stroke-linecap="round"
                                                        />
                                                    </g>
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                    className="fill-actionPrimary w-14 h-14 cursor-pointer"
                                                >
                                                    <path d="M0 73.7C0 50.67 18.67 32 41.7 32H470.3C493.3 32 512 50.67 512 73.7C512 83.3 508.7 92.6 502.6 100L336 304.5V447.7C336 465.5 321.5 480 303.7 480C296.4 480 289.3 477.5 283.6 472.1L191.1 399.6C181.6 392 176 380.5 176 368.3V304.5L9.373 100C3.311 92.6 0 83.3 0 73.7V73.7zM54.96 80L218.6 280.8C222.1 285.1 224 290.5 224 296V364.4L288 415.2V296C288 290.5 289.9 285.1 293.4 280.8L457 80H54.96z"></path>
                                                </svg>
                                            )}
                                        </Popover>
                                    </div>
                                );
                            case CONTACT:
                            case LAST_VISITED:
                                return (
                                    <div style={{ width: 130 }} className="text-left w-15p px-8">
                                        <p>{c.label}</p>
                                    </div>
                                );
                            case FOLLOW_UP:
                                return (
                                    <div style={{ width: 130 }} className="text-left w-15p px-8">
                                        <span className="flex space-x-4 items-center">
                                            <span>{c.label} </span>
                                            <Popover
                                                content={this.followUpFilter()}
                                                title={this.followUpTitle()}
                                                placement="right"
                                            >
                                                {mode === 't-followup' ||
                                                mode === 'y-followup' ||
                                                mode === 'custom-followup' ? (
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 256 256"
                                                    >
                                                        <defs></defs>
                                                        <g transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
                                                            <path
                                                                d="M 52.537 80.466 V 45.192 L 84.53 2.999 C 85.464 1.768 84.586 0 83.041 0 H 6.959 C 5.414 0 4.536 1.768 5.47 2.999 l 31.994 42.192 v 43.441 c 0 1.064 1.163 1.719 2.073 1.167 l 11.758 -7.127 C 52.065 82.205 52.537 81.368 52.537 80.466 z"
                                                                transform=" matrix(1 0 0 1 0 0) "
                                                                stroke-linecap="round"
                                                            />
                                                        </g>
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                        className="fill-actionPrimary w-14 h-14 cursor-pointer"
                                                    >
                                                        <path d="M0 73.7C0 50.67 18.67 32 41.7 32H470.3C493.3 32 512 50.67 512 73.7C512 83.3 508.7 92.6 502.6 100L336 304.5V447.7C336 465.5 321.5 480 303.7 480C296.4 480 289.3 477.5 283.6 472.1L191.1 399.6C181.6 392 176 380.5 176 368.3V304.5L9.373 100C3.311 92.6 0 83.3 0 73.7V73.7zM54.96 80L218.6 280.8C222.1 285.1 224 290.5 224 296V364.4L288 415.2V296C288 290.5 289.9 285.1 293.4 280.8L457 80H54.96z"></path>
                                                    </svg>
                                                )}
                                            </Popover>
                                        </span>
                                    </div>
                                );
                            case ABHA_IDS:
                                return (
                                    <div
                                        style={{ width: 100 }}
                                        className="flex items-center space-x-4 text-left w-10p px-8"
                                    >
                                        <p>{c.label}</p>
                                        <Popover
                                            content={this.abhaFilter()}
                                            title={this.abhaTitle()}
                                            placement="right"
                                        >
                                            {abhaFilterType ? (
                                                <svg width="14" height="14" viewBox="0 0 256 256">
                                                    <defs></defs>
                                                    <g transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
                                                        <path
                                                            d="M 52.537 80.466 V 45.192 L 84.53 2.999 C 85.464 1.768 84.586 0 83.041 0 H 6.959 C 5.414 0 4.536 1.768 5.47 2.999 l 31.994 42.192 v 43.441 c 0 1.064 1.163 1.719 2.073 1.167 l 11.758 -7.127 C 52.065 82.205 52.537 81.368 52.537 80.466 z"
                                                            transform=" matrix(1 0 0 1 0 0) "
                                                            stroke-linecap="round"
                                                        />
                                                    </g>
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                    className="fill-actionPrimary w-14 h-14 cursor-pointer"
                                                >
                                                    <path d="M0 73.7C0 50.67 18.67 32 41.7 32H470.3C493.3 32 512 50.67 512 73.7C512 83.3 508.7 92.6 502.6 100L336 304.5V447.7C336 465.5 321.5 480 303.7 480C296.4 480 289.3 477.5 283.6 472.1L191.1 399.6C181.6 392 176 380.5 176 368.3V304.5L9.373 100C3.311 92.6 0 83.3 0 73.7V73.7zM54.96 80L218.6 280.8C222.1 285.1 224 290.5 224 296V364.4L288 415.2V296C288 290.5 289.9 285.1 293.4 280.8L457 80H54.96z"></path>
                                                </svg>
                                            )}
                                        </Popover>
                                    </div>
                                );
                            case UHID:
                                return (
                                    <div style={{ width: 100 }} className="text-left w-10p px-8">
                                        <p>{c.label}</p>
                                    </div>
                                );
                            default:
                                return (
                                    <div style={{ width: 130 }} className="text-left w-15p px-8">
                                        <p>{c.label}</p>
                                    </div>
                                );
                        }
                    })}

                    <div className="text-right w-20p px-8">
                        <div className="flex items-center justify-end space-x-8">
                            <p>Actions</p>
                            <div
                                onClick={openTableConfig}
                                className="flex items-center justify-center w-16 h-16 cursor-pointer"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-16 h-16 fill-neutral-5"
                                >
                                    <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AllPtTableHeader;
